import type { LoggerBody, LoggerPayload } from '@heiland/logger'

type Prettify<T> = {
  [K in keyof T]: T[K];
} & {}

export type LogBody = Prettify<Partial<Omit<LoggerBody, 'metadata'>> & Pick<LoggerBody, 'title' | 'message'>>

declare module '#app' {
  // @ts-expect-error For some reason that might throw an error, though it's valid according to the docs
  interface NuxtApp {
    $log(level: string, body: LogBody): void
    $storeErrorLog(filePath: string, internalLoggerMessage: string, snackbarMessage: string, error: any): void
  }
}

export default defineNuxtPlugin(() => {
  const userStore = useUserStore()
  const { baseURL } = useRuntimeConfig().app

  return {
    provide: {
      log(level: string, body: LogBody) {
        let threadName = import.meta.server ? 'server' : 'client'
        if (body.threadName)
          threadName += ` - ${body.threadName}`
        const payload: Omit<LoggerPayload, 'slackWebhookUrl'> = {
          level,
          consoleOnly: true,
          body: {
            ...body,
            threadName,
            metadata: {
              customerHandle: userStore.data?.handle || '',
            },
            stack: body.stack ? JSON.parse(JSON.stringify(body.stack, Object.getOwnPropertyNames(body.stack))) : null,
          },
        }
        useFetch('/api/logger', {
          method: 'POST',
          body: payload,
          baseURL,
        })
      },
      storeErrorLog(filePath: string, internalLoggerMessage: string, snackbarMessage: string, error: any) {
        const { $snackbar, $i18n } = useNuxtApp()

        if (error.statusCode === 401)
          $snackbar.showMessage({ text: $i18n.t('snackbar.messages.auth.expired'), color: 'error', closeText: $i18n.t('close') })
        else $snackbar.showMessage({ text: snackbarMessage, color: 'error', closeText: $i18n.t('close') })

        let threadName = import.meta.server ? 'server' : 'client'
        if (filePath)
          threadName += ` - ${filePath}`

        // Only 5xx (real server failures) should trigger the error alarm; everything
        // else (4xx business errors, no-status JS errors, etc.) is a warning.
        const statusCode: unknown = error?.response?.status ?? error?.statusCode ?? error?.status
        const isServerError = typeof statusCode === 'number' && statusCode >= 500 && statusCode < 600
        const level = isServerError ? 'error' : 'warn'
        const title = isServerError ? 'Error in pinia store' : 'Warning in pinia store'

        const payload: Omit<LoggerPayload, 'slackWebhookUrl'> = {
          level,
          consoleOnly: true,
          body: {
            title,
            message: internalLoggerMessage,
            threadName,
            traceId: error.data && error.data.data ? error.data.data.traceId : undefined,
            metadata: {
              customerHandle: userStore.data?.handle || '',
            },
            stack: JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error))),
          },
        }

        useFetch('/api/logger', {
          method: 'POST',
          body: payload,
          baseURL,
        })
      },
    },
  }
})
